import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { usersApi } from '@/utils/api';
import type { User, UserRole } from '@/types';
import { Permission } from '@/utils/permissions';

interface UsersState {
  users: User[];
  currentUser: User | null;
  roles: Array<{ value: UserRole; label: string }>;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters: {
    role?: UserRole;
    isActive?: boolean;
    search?: string;
  };
}

const initialState: UsersState = {
  users: [],
  currentUser: null,
  roles: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  },
  filters: {},
};

// Async thunks
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params: {
    role?: UserRole;
    isActive?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  } = {}, { rejectWithValue }) => {
    const response = await usersApi.getAll(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch users');
    }
    return response.data;
  }
);

export const fetchUserById = createAsyncThunk(
  'users/fetchUserById',
  async (id: string, { rejectWithValue }) => {
    const response = await usersApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch user');
    }
    return (response.data as any).user;
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    additionalPermissions?: Permission[];
  }, { rejectWithValue }) => {
    const response = await usersApi.create(userData);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create user');
    }
    return (response.data as any).user;
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, data }: {
    id: string;
    data: {
      email?: string;
      firstName?: string;
      lastName?: string;
      role?: UserRole;
      isActive?: boolean;
      additionalPermissions?: Permission[];
    };
  }, { rejectWithValue }) => {
    const response = await usersApi.update(id, data);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update user');
    }
    return (response.data as any).user;
  }
);

export const activateUser = createAsyncThunk(
  'users/activateUser',
  async (id: string, { rejectWithValue }) => {
    const response = await usersApi.activate(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to activate user');
    }
    return (response.data as any).user;
  }
);

export const deactivateUser = createAsyncThunk(
  'users/deactivateUser',
  async (id: string, { rejectWithValue }) => {
    const response = await usersApi.deactivate(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to deactivate user');
    }
    return (response.data as any).user;
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string, { rejectWithValue }) => {
    const response = await usersApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete user');
    }
    return id;
  }
);

export const fetchUserRoles = createAsyncThunk(
  'users/fetchUserRoles',
  async (_, { rejectWithValue }) => {
    const response = await usersApi.getRoles();
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch roles');
    }
    return (response.data as any).roles;
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<{
      role?: UserRole;
      isActive?: boolean;
      search?: string;
      page?: number;
    }>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentUser: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = (action.payload as any).users;
        state.pagination = (action.payload as any).pagination;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create user
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.unshift(action.payload);
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser?.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // Activate user
      .addCase(activateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser?.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // Deactivate user
      .addCase(deactivateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser?.id === action.payload.id) {
          state.currentUser = action.payload;
        }
      })
      .addCase(deactivateUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(user => user.id !== action.payload);
        if (state.currentUser?.id === action.payload) {
          state.currentUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      
      // Fetch roles
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.roles = action.payload;
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters, setCurrentUser } = usersSlice.actions;
export default usersSlice.reducer;
